import { useState } from 'react';
import { Modal } from '@/shared/components/Modal';
import { Button } from '@/shared/components/Button';
import { useWorkstationStore } from './store';
import { useWorkstations } from './useWorkstations';

/**
 * Workstation picker — ADR-0039 part A, Chris's card-3 suggestion ("under their upper righthand
 * user ID let them set the location there").
 *
 * PB equivalent: the `Workstation:` dddw on `w_login`'s `dw_phfc_login`
 * (`dw_phfc_login.srd:16-17`) — label verbatim, `dddw.allowedit=no` so it is a select and never
 * a free-text field. The option list is PB's unfiltered
 * `select workstation_id, description from dbxx.workstation`
 * (`dddw_phfc_workstation.srd:11`), including the `(SYSTEM-DEFAULT)` row; picking that row
 * clears the selection exactly as `w_login.srw:281-288` nulls the column.
 *
 * The choice is device-scoped (see `store.ts`) and, for now, read by nothing but the
 * `X-PH10-Workstation` request header.
 */
export function WorkstationPickerModal({ onClose }: { onClose: () => void }) {
  const current = useWorkstationStore((s) => s.current);
  const select = useWorkstationStore((s) => s.select);
  const { data, isLoading, isError, error } = useWorkstations();
  const [pending, setPending] = useState<string>(current?.workstationId ?? '');

  function handleSave() {
    const chosen = data?.find((o) => o.workstationId === pending);
    select({
      workstationId: chosen?.workstationId ?? '',
      description: chosen?.description ?? '',
    });
    onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Workstation"
      subtitle="Applies to this browser on this machine"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || isError}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <label className="block">
          {/* Label verbatim from dw_phfc_login.srd:16 — "Workstation:" */}
          <span className="mb-1 block text-slate-600">Workstation:</span>
          <select
            aria-label="Workstation:"
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-100"
            value={pending}
            disabled={isLoading || isError}
            onChange={(e) => setPending(e.target.value)}
          >
            <option value="">—</option>
            {(data ?? []).map((o) => (
              <option key={o.workstationId} value={o.workstationId}>
                {o.workstationId}
                {o.description ? ` — ${o.description}` : ''}
              </option>
            ))}
          </select>
        </label>

        {isLoading && <p className="text-xs text-slate-500">Loading workstations…</p>}
        {isError && (
          <p className="text-xs text-red-600">
            Could not load workstations{error instanceof Error ? `: ${error.message}` : ''}.
          </p>
        )}

        <p className="text-xs text-slate-500">
          PowerBuilder asks for this at login and remembers it per machine. Choosing{' '}
          <span className="font-mono">(SYSTEM-DEFAULT)</span> or <span className="font-mono">—</span>{' '}
          clears the selection.
        </p>
      </div>
    </Modal>
  );
}
